import { DossierOverzicht, dossierOverzichtQueryKey } from "@/entities/dossier-overzicht";
import { useApiClient } from "@/infrastructure/rest";
import {
    DossierStatusDto,
    LinkDossierToSpoorwebDto,
    PostReikwijdteLandelijkDossierDto,
    PostReikwijdteStationsDossierDto,
    PostReikwijdteTrajectenDossierDto,
    DossierCreatedDto,
} from "@/shared/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { invalidateOpenDossiersQueries } from "./utilities";

export type CreateDossierInput =
    | ({ reikwijdte: "stations" } & PostReikwijdteStationsDossierDto)
    | ({ reikwijdte: "trajecten" } & PostReikwijdteTrajectenDossierDto)
    | ({ reikwijdte: "landelijk" } & PostReikwijdteLandelijkDossierDto);

export function useCreateDossier() {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ reikwijdte, ...body }: CreateDossierInput) => {
            const endpoint = `/dossiers/${reikwijdte}`;
            const createdDossier = await apiClient.post<DossierCreatedDto>(endpoint, body);

            if (!createdDossier) {
                throw new Error("Error creating dossier");
            }
            return createdDossier.dossierNummer;
        },
        onSuccess: async () => {
            // Invalidate the dossiers query to refetch after creating a new dossier
            await invalidateOpenDossiersQueries(queryClient);
        },
        onError: (error) => {
            console.error("Error creating dossier:", error);
        },
    });
}

type CloseDossierPatchRequest = Omit<DossierStatusDto, "status"> & {
    dossierNummer: string;
};

export function useCloseDossier() {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ dossierNummer, toelichting }: CloseDossierPatchRequest) => {
            const body: DossierStatusDto = { status: "Gesloten", toelichting };
            await apiClient.patch(`/dossiers/${dossierNummer}/status`, body);
        },
        onSuccess: (_data, { dossierNummer }) => {
            queryClient.setQueryData<DossierOverzicht[]>(
                dossierOverzichtQueryKey("Open"),
                (dossiers) => dossiers?.filter((d) => d.dossierId.toString() !== dossierNummer) ?? []
            );
        },
    });
}

type LinkDossierToSpoorwebOg = LinkDossierToSpoorwebDto & {
    dossierId: string;
};

export function useLinkDossierToSpoorwebOg() {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ dossierId, spoorwebId }: LinkDossierToSpoorwebOg) => {
            await apiClient.patch(`/dossiers/${dossierId}/spoorweb`, { spoorwebId });
        },
        onSuccess: async () => {
            // Invalidate the dossiers query to refetch after linking dossier with OG
            await invalidateOpenDossiersQueries(queryClient);
        },
        onError: (error) => {
            console.error("Error linking dossier:", error);
        },
    });
}
